import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Device } from "components/Navbars/DeviceStatus";
import * as Mui from "@mui/material";
import styled from "styled-components";
import { CardView } from "./CardView";
import { customGetAPI } from "services/utils/CustomAPI";
import { setReloadHRMSApi } from "services/Redux/leaveManagement";
import SkeletonGrid from "./SkeletonGrid";

export const Status = ({
  shiftId,
  date,
  departmentId,
  reloadApi,
  setReloadApi,
  setReloadAPIForCard,
  departmentMembers,
  setDepartmentMembers,
  employeeFilterData,
  setEmployeeFilterData,
  setDepartmentApi,
  workForceSelection,
  attendanceSelection,
  presentStatusSelection,
  overtimeStatusSelection,
  reCallMemberApi,
  markFiltersLoaded,
}) => {
  const dispatch = useDispatch();
  const { domain, userDetails, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  const [openSidePeek, setOpenSidePeek] = useState(false);
  const [loading, setLoading] = useState(() => !departmentMembers?.length);

  const [deviceOffine, setDeviceOffline] = useState(false);
  const [page, setPage] = useState(0);
  const [handleLoading, setHandleLoading] = useState(false);
  const [shiftDetails, setShiftDetails] = useState(() => departmentMembers || []);
  const activeRequestKeyRef = useRef(null);
  const isFetchingRef = useRef(false);

  const reCallMemberApiRef = useRef(reCallMemberApi);
  useEffect(() => {
    reCallMemberApiRef.current = reCallMemberApi;
  }, [reCallMemberApi]);

  const fetchStatus = useCallback(async (requestKey) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (activeRequestKeyRef.current === requestKey) {
      isFetchingRef.current = false;
      return;
    }

    activeRequestKeyRef.current = requestKey;
    setLoading(handleLoading ? false : true);
    const scopeStatus = { "Total": "total", "Planned": "planned", "Week Off": "weekoff", "Leave": "leave" }[workForceSelection] || workForceSelection.toLowerCase();
    let substatusValue = "";
    const SUBSTATUS_MAP = {
      "On Duty":     "onduty",
      "Overtime":    "overtime",
      "Permission":  "permission",
      "Present":     "present",
      "Absent":      "absent",
      "Active":      "active",
      "Break":       "break",
      "Out":         "out",
      "Missing":     "missing",
      "Late":        "late",
      "Early":       "early",
      "Only OT":     "only_ot",
      "Approved OT": "approved_ot",
      "Pending OT":  "pending_ot",
      "Rejected OT": "rejected_ot",
    };
    if (attendanceSelection !== "") {
      substatusValue = SUBSTATUS_MAP[attendanceSelection] || attendanceSelection.toLowerCase().replace(/\s+/g, "_");
    }
    if (presentStatusSelection !== "") {
      substatusValue = SUBSTATUS_MAP[presentStatusSelection] || presentStatusSelection.toLowerCase().replace(/\s+/g, "_");
    } else if (attendanceSelection === "Overtime" && overtimeStatusSelection !== "") {
      substatusValue = SUBSTATUS_MAP[overtimeStatusSelection] || overtimeStatusSelection.toLowerCase().replace(/\s+/g, "_");
    }
    const params = new URLSearchParams({
      department_id: departmentId,
      date,
      shift_id: shiftId,
      user_id: userDetails.id,
      status: scopeStatus,
      substatus: substatusValue,
      company_id: topbarCompanyId,
    });
    const url = `${domain}attendance-filter-view/?${params.toString()}`;
    try {
      const res = await customGetAPI(url);
      if (res.ok) {
        if (res.code === 202) {
          setDeviceOffline(true);
          setHandleLoading(false);
          dispatch(setReloadHRMSApi(false));
        } else {
          dispatch(setReloadHRMSApi(false));
          const updatedUserDetails = res?.responseDetails?.user_status_details;
          setShiftDetails(updatedUserDetails);
          setDepartmentMembers(updatedUserDetails);
          setEmployeeFilterData(updatedUserDetails);
          setDeviceOffline(false);
          setHandleLoading(false);
          setPage(0);
        }
        setReloadApi(false);
        setLoading(false);
        if (markFiltersLoaded) markFiltersLoaded();
      }
    } finally {
      isFetchingRef.current = false;
      if (activeRequestKeyRef.current === requestKey) {
        activeRequestKeyRef.current = null;
      }
    }
  }, [
    attendanceSelection,
    date,
    departmentId,
    dispatch,
    domain,
    handleLoading,
    overtimeStatusSelection,
    presentStatusSelection,
    markFiltersLoaded,
    setDepartmentMembers,
    setEmployeeFilterData,
    setReloadApi,
    shiftId,
    topbarCompanyId,
    userDetails.id,
    workForceSelection,
  ]);

  useEffect(() => {
    if (reloadApi) {
      const requestKey = [
        departmentId,
        date,
        shiftId,
        workForceSelection,
        attendanceSelection,
        presentStatusSelection,
        overtimeStatusSelection,
        topbarCompanyId,
        userDetails.id,
      ].join("|");
      fetchStatus(requestKey);
    }
  }, [
    attendanceSelection,
    date,
    departmentId,
    fetchStatus,
    overtimeStatusSelection,
    presentStatusSelection,
    reloadApi,
    shiftId,
    topbarCompanyId,
    userDetails.id,
    workForceSelection,
  ]);

  return (
    <React.Fragment>
      {deviceOffine ? (
        <>
          <Mui.Grid sx={{ paddingTop: 1 }}>
            <Device />
          </Mui.Grid>
        </>
      ) : (
        ""
      )}
      <StyledGrid container sx={{ paddingTop: deviceOffine ? 0 : 4 }}>
        <ContentGrid
          item
          xs={12}
          sx={{
            paddingTop: {
              xs: deviceOffine ? 0 : 5,
              md: deviceOffine ? 0 : 7,
              sm: deviceOffine ? 0 : 8,
              lg: 0,
            },
            width: "100%",
            minWidth: 0,
          }}
        >
          {loading ? (
            <SkeletonGrid />
          ) : (
            !deviceOffine && (
              <CardView
                departmentMembers={departmentMembers}
                setDepartmentMembers={setDepartmentMembers}
                filterDepartMemberList={employeeFilterData}
                page={page}
                setPage={setPage}
                loading={loading}
                setShiftDetails={setShiftDetails}
                setOpenSidePeek={setOpenSidePeek}
                openSidePeek={openSidePeek}
                shiftDetails={shiftDetails}
                fetchStatus={fetchStatus}
                departmentId={departmentId}
                setDepartmentApi={setDepartmentApi}
                reCallMemberApi={reCallMemberApi}
              />
            )
          )}
        </ContentGrid>
      </StyledGrid>
    </React.Fragment>
  );
};

const StyledGrid = styled(Mui.Grid)`
  min-height: 100%;
  overflow: hidden;
`;

const ContentGrid = styled(Mui.Grid)`
  width: 100%;
  max-width: 100%;
`;
